import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useShallow } from 'zustand/shallow';
import type { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { useGeo } from '@repo/ui/GeoTargeting/GeoProvider';
import { useFilterEvents } from '@repo/ui/OffersFilter/useFilterEvents';
import { useOffersFilterStore } from '../store';
import { useFilterLocationOptions } from '../store/hooks';
import type { OffersFilterStoreState } from '../store/store';

export const MobileSelectLocation = ({
  actionSection,
  pageIdentifier,
}: {
  actionSection: string;
  pageIdentifier: string;
}) => {
  const locationOptions = useFilterLocationOptions();
  const { regionCode, setRegionCode } = useGeo();

  const { handleUpdateFilterFormState, handleApplyFilters, appliedFilters } = useOffersFilterStore(
    useShallow((state: OffersFilterStoreState) => ({
      handleUpdateFilterFormState: state.handleUpdateFilterFormState,
      handleApplyFilters: state.handleApplyFilters,
      appliedFilters: state.appliedFilters,
    })),
  );

  const { updateFiltersEvent } = useFilterEvents({
    actionSection,
    pageIdentifier,
    additionalAttributes: {},
  });

  const handleLocationChange = (selectedLocation: string) => {
    setTimeout(() => {
      handleUpdateFilterFormState({
        selectedLocation: selectedLocation,
      });
      setRegionCode(selectedLocation as StateAbbreviation);
      const newState = handleApplyFilters();
      if (!newState) return;
      updateFiltersEvent({
        additional_attributes: {
          location: selectedLocation,
          product_types: newState.appliedFilters.selectedProductTypes,
        },
      });
    }, 100);
  };

  const selectedLocation = appliedFilters?.selectedLocation ?? regionCode;

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        border: 0,
        '&.Mui-expanded': {
          borderBottom: 0,
        },
        '&.MuiAccordion-root': {
          borderBottom: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
          },
          borderBottom: '1px solid',
          borderBottomColor: 'neutral.lighter',
          px: 3,
          mb: 0.5,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: '#0069B9',
          },
          '&:focus:hover': {
            outline: '2px solid !important',
            outlineColor: '#0069B9 !important',
          },
        }}
      >
        <Typography sx={{ fontSize: 14, color: 'text.secondary', fontWeight: 600 }}>
          LOCATION
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {locationOptions.map(option => (
            <ListItemButton
              key={option.value}
              selected={selectedLocation === option.value}
              sx={{
                px: 3,
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  ':hover': { backgroundColor: 'neutral.lightest' },
                  ':focus-visible': { backgroundColor: 'neutral.lightest' },
                },
              }}
              onClick={() => handleLocationChange(option.value)}
            >
              <ListItemText
                primary={option.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontSize: 14,
                    color: 'text.secondary',
                  },
                }}
              />
              {selectedLocation === option.value && (
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <CheckIcon fontSize="small" sx={{ color: 'greens.main' }} />
                </ListItemIcon>
              )}
            </ListItemButton>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
